import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  Calendar,
  Clock,
  Filter,
  FileText,
  Loader2
} from "lucide-react";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { fmtTz } from "../lib/time";

export default function EmployeeRecords() {
  const { authAxios } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(fmtTz(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(fmtTz(new Date(), 'yyyy-MM-dd'));

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get('/time/history', {
        params: { start_date: startDate, end_date: endDate }
      });
      setRecords(res.data);
    } catch (error) {
      console.error('Failed to fetch records:', error);
    } finally {
      setLoading(false);
    }
  }, [authAxios, startDate, endDate]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const formatMinutes = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}m`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    return fmtTz(isoString, 'HH:mm');
  };

  const setDateRange = (range) => {
    const today = new Date();
    switch (range) {
      case 'week':
        setStartDate(format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'));
        setEndDate(format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'));
        break;
      case 'month':
        setStartDate(format(startOfMonth(today), 'yyyy-MM-dd'));
        setEndDate(format(endOfMonth(today), 'yyyy-MM-dd'));
        break;
      case 'last30':
        setStartDate(fmtTz(subDays(today, 30), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      default:
        break;
    }
  };

  const totalWorkMinutes = records.reduce((sum, r) => sum + (r.total_work_minutes || 0), 0);
  const totalBreakMinutes = records.reduce((sum, r) => sum + (r.total_break_minutes || 0), 0);
  const daysWorked = records.filter(r => r.clock_in).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="employee-records">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-stone-900 font-heading">My Time Records</h1>
        <p className="text-stone-500 mt-1">View your work history and hours</p>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-600">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="start-date-input"
                  className="bg-stone-50 border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-600">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="end-date-input"
                  className="bg-stone-50 border-stone-200"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => setDateRange('week')} className="text-xs">
                This Week
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDateRange('month')} className="text-xs">
                This Month
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDateRange('last30')} className="text-xs">
                Last 30 Days
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Total Work</p>
                <p className="text-2xl font-bold text-stone-900 font-mono">{formatMinutes(totalWorkMinutes)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Days Worked</p>
                <p className="text-2xl font-bold text-stone-900 font-mono">{daysWorked}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-stone-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Total Breaks</p>
                <p className="text-2xl font-bold text-stone-900 font-mono">{formatMinutes(totalBreakMinutes)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Records Table */}
      <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Filter className="w-5 h-5 text-stone-400" />
            Time Entries
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              No records found for the selected date range
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-stone-100">
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Clock In</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Clock Out</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Work Time</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Break Time</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record) => (
                    <TableRow key={record.id} className="border-stone-100 hover:bg-stone-50">
                      <TableCell className="font-medium">
                        {fmtTz(record.date, 'EEE, MMM d')}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{formatTime(record.clock_in)}</TableCell>
                      <TableCell className="font-mono text-sm">{formatTime(record.clock_out)}</TableCell>
                      <TableCell className="font-mono text-sm text-emerald-600 font-medium">
                        {formatMinutes(record.total_work_minutes || 0)}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-amber-600">
                        {formatMinutes(record.total_break_minutes || 0)}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline"
                          className={
                            record.status === 'working' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                            record.status === 'on_break' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                            'bg-stone-100 text-stone-600 border-stone-200'
                          }
                        >
                          {record.status === 'working' ? 'Working' : record.status === 'on_break' ? 'On Break' : 'Off'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
